import { useState, useRef, useEffect, useMemo } from "react"
import toast from "react-hot-toast"
import { lumi } from "../lib/lumi"
import type { EquipmentIssue, IssueInput, IssueStatus, IssueAttachment, ReferencedUser } from "../hooks/useEquipmentIssues"
import type { EquipmentUnit } from "../hooks/useEquipments"
import { useSelectableUsers } from "../hooks/useEquipmentNotify"

const COMMON_UNIT_VALUE = "__common__"

// 호기 표시 라벨 생성
function unitLabelOf(u: EquipmentUnit) {
  return u.serialNumber ? `${u.unitName} (S/N ${u.serialNumber})` : u.unitName
}

interface Props {
  initial?: EquipmentIssue | null
  units?: EquipmentUnit[]
  onClose: () => void
  onSubmit: (data: IssueInput) => Promise<void>
}

const STATUS_OPTIONS: { value: IssueStatus; label: string }[] = [
  { value: "issued", label: "이슈 등록" },
  { value: "checking", label: "확인중" },
  { value: "resolved", label: "조치완료" },
  { value: "unavailable", label: "조치불가" },
]

const MAX_FILES = 5
const MAX_SIZE = 30 * 1024 * 1024 // 30MB

// 첨부 파일 유형 판별
function isImageAttachment(a: { type?: string; name?: string }) {
  if (a.type === "image") return true
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(a.name || "")
}

export function EquipmentIssueFormModal({ initial, units = [], onClose, onSubmit }: Props) {
  const isEdit = !!initial
  const panelRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { users } = useSelectableUsers()

  const [unitValue, setUnitValue] = useState<string>(initial?.unitId || COMMON_UNIT_VALUE)
  const [title, setTitle] = useState(initial?.title || "")
  const [issueContent, setIssueContent] = useState(initial?.issueContent || "")
  const [tags, setTags] = useState<string[]>(initial?.tags || [])
  const [tagInput, setTagInput] = useState("")
  const [expectedCause, setExpectedCause] = useState(initial?.expectedCause || "")
  const [possibleAction, setPossibleAction] = useState(initial?.possibleAction || "")
  const [status, setStatus] = useState<IssueStatus>(initial?.status || "issued")
  const [actualCost, setActualCost] = useState<string>(
    initial?.actualCost ? String(initial.actualCost) : ""
  )
  const [fieldComment, setFieldComment] = useState(initial?.fieldComment || "")
  const [attachments, setAttachments] = useState<IssueAttachment[]>(initial?.attachments || [])
  const [referencedUsers, setReferencedUsers] = useState<ReferencedUser[]>(
    initial?.referencedUsers || []
  )
  const [refQuery, setRefQuery] = useState("")
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [onClose])

  // 파일 업로드 (사진/문서, 최대 5개)
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    if (attachments.length + files.length > MAX_FILES) {
      toast.error(`첨부파일은 최대 ${MAX_FILES}개까지 업로드할 수 있습니다.`)
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    const valid: File[] = []
    for (const f of files) {
      if (f.size > MAX_SIZE) {
        toast.error(`파일 용량이 너무 큽니다 (최대 30MB): ${f.name}`)
        continue
      }
      valid.push(f)
    }
    if (valid.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    setUploading(true)
    try {
      const results = await lumi.tools.file.upload(valid)
      const uploaded: IssueAttachment[] = []
      for (const r of results) {
        if (r.fileUrl) {
          const isImg = (r.fileName || "").match(/\.(png|jpe?g|gif|webp|bmp|svg)$/i)
          uploaded.push({ url: r.fileUrl, name: r.fileName, type: isImg ? "image" : "file" })
        } else {
          toast.error(`업로드 실패: ${r.fileName} (${r.uploadError || "알 수 없는 오류"})`)
        }
      }
      if (uploaded.length > 0) {
        setAttachments((prev) => [...prev, ...uploaded])
        toast.success(`${uploaded.length}개의 파일이 업로드되었습니다.`)
      }
    } catch (error: any) {
      console.error("파일 업로드 실패:", error)
      const msg = String(error?.message || "")
      if (msg.includes("quota")) {
        toast.error("저장 용량 한도를 초과했습니다. 일부 파일을 정리한 후 다시 시도하세요.")
      } else {
        toast.error("파일 업로드 중 오류가 발생했습니다.")
      }
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const removeAttachment = (url: string) => {
    setAttachments((prev) => prev.filter((a) => a.url !== url))
  }

  // 태그 추가/제거 (Enter 또는 쉼표로 추가, 중복/공백 제거)
  const addTag = (raw: string) => {
    const t = raw.trim().replace(/^#/, "").trim()
    if (!t) return
    setTags((prev) => (prev.includes(t) ? prev : [...prev, t]))
    setTagInput("")
  }

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      addTag(tagInput)
    } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
      setTags((prev) => prev.slice(0, -1))
    }
  }

  const removeTag = (tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag))
  }

  // 참조자 추가/제거
  const refCandidates = useMemo(() => {
    const selectedIds = new Set(referencedUsers.map((u) => u.userId))
    const q = refQuery.trim().toLowerCase()
    return users
      .filter((u) => !selectedIds.has(u.userId))
      .filter(
        (u) =>
          !q ||
          u.userName.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          (u.teamName || "").toLowerCase().includes(q)
      )
      .slice(0, 6)
  }, [users, referencedUsers, refQuery])

  const addReference = (u: { userId: string; userName: string; email: string; teamName?: string }) => {
    setReferencedUsers((prev) => [
      ...prev,
      { userId: u.userId, userName: u.userName, email: u.email, teamName: u.teamName },
    ])
    setRefQuery("")
  }

  const removeReference = (userId: string) => {
    setReferencedUsers((prev) => prev.filter((u) => u.userId !== userId))
  }

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("이슈 제목을 입력하세요.")
      return
    }
    if (!issueContent.trim()) {
      toast.error("이슈 내용을 입력하세요.")
      return
    }
    setSubmitting(true)
    try {
      const isCommon = unitValue === COMMON_UNIT_VALUE
      const selectedUnit = units.find((u) => u.unitId === unitValue)
      const unitLabel = isCommon ? "공통항목" : selectedUnit ? unitLabelOf(selectedUnit) : "공통항목"
      await onSubmit({
        unitId: isCommon ? "" : unitValue,
        unitLabel,
        title: title.trim(),
        issueContent: issueContent.trim(),
        expectedCause: expectedCause.trim(),
        possibleAction: possibleAction.trim(),
        status,
        actualCost: actualCost ? Number(actualCost) : 0,
        fieldComment: fieldComment.trim(),
        tags,
        attachments,
        referencedUsers,
      })
      toast.success(isEdit ? "이슈가 수정되었습니다." : "이슈가 등록되었습니다.")
      onClose()
    } catch (error) {
      console.error("이슈 저장 실패:", error)
      toast.error("저장 중 오류가 발생했습니다.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={panelRef}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg sm:max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? "이슈 수정" : "이슈 등록"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* 공통/호기 분류 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              분류 <span className="text-red-500">*</span>
            </label>
            <select
              value={unitValue}
              onChange={(e) => setUnitValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white"
            >
              <option value={COMMON_UNIT_VALUE}>공통항목</option>
              {units.map((u) => (
                <option key={u.unitId} value={u.unitId}>
                  {unitLabelOf(u)}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              특정 호기에 해당하는 이슈면 호기를 선택하세요. 장비 공통 이슈는 '공통항목'으로 등록됩니다.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              이슈 제목 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="이슈를 한 줄로 요약한 제목 (알림 메일 제목으로 사용됩니다)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              이슈 내용 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={issueContent}
              onChange={(e) => setIssueContent(e.target.value)}
              rows={3}
              placeholder="파손 내용 또는 부품 등 이슈 내용을 입력하세요"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-y min-h-[80px]"
            />
          </div>

          {/* 태그 입력 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">태그</label>
            <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-700 text-xs rounded-full"
                >
                  #{tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="text-orange-400 hover:text-orange-600"
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={() => addTag(tagInput)}
                placeholder={tags.length === 0 ? "태그 입력 후 Enter (예: 누수, 베어링)" : ""}
                className="flex-1 min-w-[120px] px-1 py-1 text-sm outline-none bg-transparent"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Enter 또는 쉼표로 태그를 추가할 수 있습니다.</p>
          </div>

          {/* 첨부파일 업로드 (사진/문서, 최대 5개) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              첨부파일 <span className="text-gray-400 font-normal">({attachments.length}/{MAX_FILES})</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {attachments.map((a) => (
                isImageAttachment(a) ? (
                  <div key={a.url} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 group">
                    <img src={a.url} alt={a.name} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.url)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center bg-black/60 text-white rounded-full text-xs hover:bg-black/80"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div key={a.url} className="relative w-20 h-20 flex flex-col items-center justify-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
                    <svg className="w-7 h-7 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="text-[9px] text-gray-600 text-center leading-tight line-clamp-2 break-all px-0.5">{a.name}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.url)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center bg-black/60 text-white rounded-full text-xs hover:bg-black/80"
                    >
                      ×
                    </button>
                  </div>
                )
              ))}
              {attachments.length < MAX_FILES && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:border-orange-400 hover:text-orange-500 disabled:opacity-50"
                >
                  {uploading ? (
                    <div className="w-5 h-5 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span className="text-[10px] mt-0.5">파일 추가</span>
                    </>
                  )}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">사진 및 문서 파일을 첨부할 수 있습니다. (파일당 최대 30MB)</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.hwp,.zip"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* 참조자 지정 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">참조자 지정</label>
            {referencedUsers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {referencedUsers.map((u) => (
                  <span
                    key={u.userId}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded-full"
                  >
                    {u.userName}
                    {u.teamName ? <span className="text-orange-400">· {u.teamName}</span> : null}
                    <button
                      type="button"
                      onClick={() => removeReference(u.userId)}
                      className="ml-0.5 text-orange-400 hover:text-orange-600"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="relative">
              <input
                type="text"
                value={refQuery}
                onChange={(e) => setRefQuery(e.target.value)}
                placeholder="이름/이메일/팀으로 검색하여 참조자 추가"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none text-sm"
              />
              {refQuery.trim() && refCandidates.length > 0 && (
                <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {refCandidates.map((u) => (
                    <li key={u.userId}>
                      <button
                        type="button"
                        onClick={() => addReference(u)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50"
                      >
                        <span className="font-medium text-gray-700">{u.userName}</span>
                        {u.teamName ? <span className="text-gray-400"> · {u.teamName}</span> : null}
                        <span className="block text-xs text-gray-400">{u.email}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">참조자에게는 이슈 등록/상태 변경 시 이메일이 발송됩니다.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">예상원인 및 History</label>
            <textarea
              value={expectedCause}
              onChange={(e) => setExpectedCause(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-y min-h-[64px]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">가능한 조치</label>
            <textarea
              value={possibleAction}
              onChange={(e) => setPossibleAction(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-y min-h-[64px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">조치 상태</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as IssueStatus)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">실제 소요 비용(원)</label>
              <input
                type="number"
                value={actualCost}
                onChange={(e) => setActualCost(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">현장 코멘트</label>
            <textarea
              value={fieldComment}
              onChange={(e) => setFieldComment(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-y min-h-[64px]"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || uploading}
            className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-orange-600 to-amber-600 rounded-lg hover:from-orange-700 hover:to-amber-700 disabled:opacity-60"
          >
            {submitting ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>
    </div>
  )
}
